/**
 * SourceUploads — champs d'upload d'image source (img2img/inpaint source,
 * masque, carte ControlNet, IP-Adapter) avec aperçu et référence managée
 * côté serveur (POST /api/media/upload-*).
 */
import { APIClient } from "../../api/client.js";
import { toast } from "../../components/Toast.js";
import { h } from "../../components/dom.js";
import type { ControlnetType, SourceUploadResponse } from "../../types/index.js";

export interface SourceUploadOptions {
	label: string;
	help?: string;
	/** Résolu à l'envoi → upload via /api/media/upload-control-image?control_type=. */
	getControlType?: () => ControlnetType | undefined;
	onUploaded?: () => void;
}

export interface SourceUploadHandle {
	element: HTMLElement;
	/** source_ref managé côté serveur, ou null si aucun fichier. */
	getRef(): string | null;
	reset(): void;
}

export function sourceImageUpload(options: SourceUploadOptions): SourceUploadHandle {
	let ref: string | null = null;
	let previewUrl: string | null = null;

	const preview = h("img", { class: "upload-preview", alt: "", hidden: true });
	const status = h("small", { class: "help-text", "aria-live": "polite" });
	const fileInput = h("input", {
		type: "file",
		accept: "image/png,image/jpeg,image/webp",
	}) as HTMLInputElement;

	async function onFile(file: File): Promise<void> {
		status.textContent = "Envoi…";
		try {
			let response: SourceUploadResponse;
			const controlType = options.getControlType?.();
			if (controlType) {
				response = await APIClient.uploadControlImage(file, controlType);
			} else {
				response = await APIClient.uploadSource(file);
			}
			ref = response.source_ref;
			if (previewUrl) URL.revokeObjectURL(previewUrl);
			previewUrl = response.url;
			preview.src = response.url;
			preview.hidden = false;
			status.textContent = `${response.width}×${response.height}`;
			options.onUploaded?.();
		} catch (error) {
			ref = null;
			status.textContent = "";
			toast(error instanceof Error ? error.message : "Échec de l'envoi de l'image", "error");
		}
	}

	fileInput.addEventListener("change", () => {
		const file = fileInput.files?.[0];
		if (file) void onFile(file);
	});

	const element = h(
		"div",
		{ class: "source-upload" },
		h("label", { class: "field-label" }, options.label),
		h("div", { class: "file-input-row" }, fileInput, status),
		preview,
		options.help ? h("p", { class: "help-text" }, options.help) : null
	);

	return {
		element,
		getRef: () => ref,
		reset: () => {
			ref = null;
			preview.hidden = true;
			fileInput.value = "";
		},
	};
}
